import { unwrapData } from '@wapharma/shared';
import { apiGet } from './api';

export type ListMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  facets?: Record<string, unknown>;
};

export type ApiListResult<T> = {
  items: T[];
  meta: ListMeta | null;
};

function metaFromEnvelope(raw: unknown): ListMeta | null {
  if (!raw || typeof raw !== 'object' || !('meta' in raw)) return null;
  const meta = (raw as { meta: unknown }).meta;
  if (!meta || typeof meta !== 'object') return null;
  return meta as ListMeta;
}

/**
 * GET a paginated or plain list — unwraps `{ data }` / `{ items }` envelopes.
 */
export async function apiGetList<T>(
  path: string,
  token?: string,
  signal?: AbortSignal,
): Promise<ApiListResult<T>> {
  const raw = await apiGet<unknown>(path, token, signal);

  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: unknown }).data)) {
    return { items: (raw as { data: T[] }).data, meta: metaFromEnvelope(raw) };
  }

  const unwrapped = unwrapData<unknown>(raw);
  if (Array.isArray(unwrapped)) {
    return { items: unwrapped as T[], meta: metaFromEnvelope(raw) };
  }

  if (
    unwrapped &&
    typeof unwrapped === 'object' &&
    'items' in unwrapped &&
    Array.isArray((unwrapped as { items: unknown }).items)
  ) {
    const env = unwrapped as { items: T[]; meta?: ListMeta };
    return { items: env.items, meta: env.meta ?? metaFromEnvelope(raw) };
  }

  return { items: [], meta: metaFromEnvelope(raw) };
}

/** GET list endpoint — returns items array (unwraps `{ data }` envelopes). */
export async function apiGetListSimple<T>(
  path: string,
  token?: string,
  signal?: AbortSignal,
): Promise<T[]> {
  const { items } = await apiGetList<T>(path, token, signal);
  return items;
}
